import React, { useState, useEffect } from 'react';
import { useForm, usePage, Head } from '@inertiajs/react';
import { TextInput, Textarea, Button, Box, Text, MultiSelect, MantineProvider } from '@mantine/core';
import { Notifications, showNotification } from '@mantine/notifications';
import { IconCheck, IconAlertCircle } from '@tabler/icons-react';
import FormLayout from '../../components/FormLayout'; // Provides common layout/styling
import ImageUploadModal from '../../components/ImageUploadModal'; // Modal for image uploads

import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';

// Interface for the data within the form
interface UserFormData {
  username: string;
  email: string;
  bio: string;
  avatar: File | null;
  post_seo_image: File | null;
  role_ids: string[];
}

// Interface for props passed via Inertia from the controller
interface EditUserProps {
  user: {
    id: number;
    username: string;
    email: string;
    bio?: string;
    avatar_url?: string | null;
    post_seo_image_url?: string | null;
    // Additional associations if needed
    role_ids: string[];
    event_ids: string[];
    artist_ids: string[];
    organizer_ids: string[];
    private_lesson_feature_flag: boolean;
  };
  available_roles: Array<{ value: string; label: string }>;
  all_events: Array<{ value: string; label: string }>;
  all_artists: Array<{ value: string; label: string }>;
  all_organizers: Array<{ value: string; label: string }>;
  current_user_roles: string[];
  can_delete: boolean;
  update_url: string;
  delete_url: string;
  errors?: Partial<Record<keyof UserFormData | string, string>>;
}

export default function EditUser() {
  const { user, update_url, available_roles, errors: initialErrors } = usePage<EditUserProps>().props;

  // Use Inertia's useForm hook for form state management
  const { data, setData, put, processing, errors, recentlySuccessful } = useForm<UserFormData>({
    username: user.username,
    email: user.email,
    bio: user.bio || '',
    avatar: null, // For file uploads; initially null
    post_seo_image: null, // For file uploads; initially null
    role_ids: user.role_ids || [],
  });

  // State for preview images
  const [avatarPreview, setAvatarPreview] = useState<string | null>(user.avatar_url);
  const [postSeoPreview, setPostSeoPreview] = useState<string | null>(user.post_seo_image_url);

  // Update preview if the form submission is successful or if prop image URLs change
  useEffect(() => {
    if (recentlySuccessful) {
      setAvatarPreview(user.avatar_url);
      setPostSeoPreview(user.post_seo_image_url);
    }
  }, [user.avatar_url, user.post_seo_image_url, recentlySuccessful]);

  // Handlers to update file inputs and previews
  const handleAvatarChange = (file: File) => {
    setData('avatar', file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const handlePostSeoChange = (file: File) => {
    setData('post_seo_image', file);
    setPostSeoPreview(URL.createObjectURL(file));
  };

  // Form submission using Inertia's put method
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    put(update_url, {
      onSuccess: () => {
        showNotification({
          color: 'green',
          title: 'Update Successful',
          message: 'User was successfully updated!',
          icon: <IconCheck />,
          autoClose: 4000,
          position: 'top-right',
        });
      },
      onError: (errs) => {
        console.error('Submission error:', errs);
        showNotification({
          color: 'red',
          title: 'Update Failed',
          position: 'top-right',
          message: 'Please check the errors below.',
          icon: <IconAlertCircle />,
          autoClose: 5000,
        });
      },
    });
  };

  return (
    <MantineProvider>
      <Notifications />
      <FormLayout title="Edit Your User Account">
        <Head title={`Edit ${user.username}`} />

        <form onSubmit={handleSubmit}>
          {/* Avatar image upload */}
          <ImageUploadModal previewUrl={avatarPreview} onImageChange={handleAvatarChange} />
          {errors.avatar && (
            <Text c="red" size="sm" mt={-10} mb={10}>
              {errors.avatar}
            </Text>
          )}

          <TextInput
            label="Username"
            value={data.username}
            onChange={(e) => setData('username', e.target.value)}
            mt="md"
            required
            error={errors.username}
            disabled={processing}
          />

          <TextInput
            label="Email"
            value={data.email}
            onChange={(e) => setData('email', e.target.value)}
            mt="md"
            required
            error={errors.email}
            disabled={processing}
          />

          <Textarea
            label="Bio"
            value={data.bio}
            onChange={(e) => setData('bio', e.target.value)}
            my="lg"
            error={errors.bio}
            disabled={processing}
          />

          <MultiSelect
            data={available_roles}
            label="Roles"
            searchable
            hidePickedOptions
            placeholder={data.role_ids.length === 0 ? 'Select roles' : ''}
            value={data.role_ids}
            onChange={(value) => setData('role_ids', value)}
            mt="md"
            error={errors.role_ids}
          />

          {errors.post_seo_image && (
            <Text c="red" size="sm" mt={-10} mb={10}>
              {errors.post_seo_image}
            </Text>
          )}

          <Button type="submit" mt="xl" loading={processing}>
            Update User
          </Button>
        </form>
      </FormLayout>
    </MantineProvider>
  );
}
